import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { type AdActivityDataType, useAdActivity } from '@/api/ad';
import { TRAFFIC_TYPES } from '@/api/common';
import PaidSearchKeywordActivityTable from '@/app/paid-search/PaidSearchKeywordActivityTable';
import { Toggle } from '@/components/buttons';
import { RangeFilter } from '@/components/filters';
import {
  type InstantSearchDataTableColumnDef,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { usePageFilterContext } from '@/components/page';
import { Link, SupportLink } from '@/components/typography';
import {
  AD_SCORE_MAX_LIMIT,
  AUDIENCE_PRIMARY,
  BOT_PRIMARY,
  CHANNEL_ID_PAID_SEARCH,
  TAM_PRIMARY,
  UNKNOWN_PRIMARY,
  VISITS_PRIMARY,
} from '@/constants';
import {
  CampaignFilter,
  KeywordFilter,
  KeywordGroupFilter,
  KeywordMatchTypeFilter,
  VendorFilter,
} from '@/features/filters';
import { StackModeToggleButton } from '@/features/StackModeToggleButton';
import { KeywordMatchTypeTag } from '@/features/tags';
import {
  ViewThroughPercentCell,
  VisitsTableBarCell,
  VIEW_THROUGH_VISITS_METRIC_FIELDS,
} from '@/features/VisitsTableBarCell';
import useMaxVisitsStats from '@/features/VisitsTableBarCell/useMaxVisitsStats';
import didClickOnStickyPopover from '@/helper/didClickOnStickyPopover';
import { numberFormat } from '@/helper/numberFormatter';
import { TableState } from '@/providers/TableState';
import { ROUTES } from '@/router';

const DEFAULT_HIDDEN_COLUMNS = [
  'adClicks',
  'countPipelineInfluenced',
  'countClosedwonInfluenced',
  'companiesReached',
  'leadsInfluenced',
  'pipelineFirstTouchAttribution',
  'closedWonFirstTouchAttribution',
  'pipelineLastTouchAttribution',
];

type Props = {
  disabledSeries: TRAFFIC_TYPES[] | undefined;
  menuSelectorOpen: boolean;
  advancedFiltersOpen: boolean;
  onMetricSelectorOpen: (open: boolean) => void;
  onAdvancedFiltersOpen: (open: boolean) => void;
  onScoreAds: (adIds: string[]) => void;
};

const PaidSearchKeywordActivityTableContainer = ({
  disabledSeries,
  menuSelectorOpen,
  advancedFiltersOpen,
  onMetricSelectorOpen,
  onAdvancedFiltersOpen,
  onScoreAds,
}: Props) => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const { queryState } = useInstantSearchState();
  const [selectedAds, setSelectedAds] = useState<AdActivityDataType[]>([]);
  const [visitsMode, setVisitsMode] = useState<'stack' | 'percent'>('percent');
  const [includeViewThroughVisits, setIncludeViewThroughVisits] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<AdActivityDataType | null>(null);
  const [stickyRow, setStickyRow] = useState<{
    element: HTMLElement;
    record: AdActivityDataType;
  } | null>(null);
  const {
    adActivity: data,
    totalResults,
    isLoading,
    error,
  } = useAdActivity(
    getSolQueryParamsNewFromQueryState(queryState),
    startDate,
    endDate,
    audience?.id,
    {
      channelIds: [CHANNEL_ID_PAID_SEARCH],
    },
  );

  const maxVisits = useMaxVisitsStats({ data, audience, disabledSeries });
  const maxVisitsWithViewThrough = useMaxVisitsStats({
    data,
    audience,
    disabledSeries,
    metricFields: VIEW_THROUGH_VISITS_METRIC_FIELDS,
    sumWithBaseMetrics: true,
  });
  const visitsBarMax = includeViewThroughVisits ? maxVisitsWithViewThrough : maxVisits;

  const isMaxCompareLimit = selectedAds.length === AD_SCORE_MAX_LIMIT;

  const sortFields = ROUTES.paidSearch.searchParams['p.sortBy'].enumValues;

  const columns = useMemo<InstantSearchDataTableColumnDef<AdActivityDataType>[]>(
    () => [
      {
        header: t`Keyword`,
        id: 'adId',
        enableSorting: sortFields.includes('ad.name'),
        minSize: 250,
        flex: 1,
        instantSearch: {
          sortField: 'ad.name',
        },
        metricSelector: false,
        filterComponent: (props) => <KeywordFilter {...props} />,
        cell: ({ row }) => row.original.ad.name,
      },
      {
        header: t`Match Type`,
        id: 'ad.matchType',
        enableSorting: sortFields.includes('ad.matchType'),
        size: 100,
        metricSelector: false,
        filterComponent: (props) => <KeywordMatchTypeFilter {...props} />,
        cell: ({ row }) => <KeywordMatchTypeTag matchType={row.original.ad.matchType} />,
      },
      {
        header: t`Ad Group`,
        id: 'adGroupId',
        enableSorting: sortFields.includes('adGroup.name'),
        minSize: 175,
        flex: 1,
        instantSearch: {
          sortField: 'adGroup.name',
        },
        metricSelector: false,
        filterComponent: (props) => <KeywordGroupFilter {...props} />,
        cell: ({ row }) => row.original.ad.adGroup.name,
      },
      {
        header: t`Campaign`,
        id: 'campaign.id',
        enableSorting: sortFields.includes('campaign.id'),
        minSize: 175,
        flex: 1,
        metricSelector: false,
        filterComponent: (props) => (
          <CampaignFilter {...props} channelId={CHANNEL_ID_PAID_SEARCH} />
        ),
        cell: ({ row }) => row.original.ad.adGroup.campaign.name,
      },
      {
        header: t`Vendor`,
        id: 'vendor.id',
        enableSorting: sortFields.includes('vendor.id'),
        minSize: 100,
        flex: 1,
        metricSelector: false,
        filterComponent: (props) => <VendorFilter {...props} channelId={CHANNEL_ID_PAID_SEARCH} />,
        cell: ({ row }) => row.original.ad.adGroup.campaign.vendor.name,
      },
      {
        header: t`Spend`,
        id: 'spend',
        accessorFn: (row) => row.spend,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('spend'),
        size: 110,
        infoPopover: (
          <Trans>
            Spend is reported either through your vendor's API connection or from manually
            calculated spend entered by your team on the{' '}
            <Link variant="body2" color="static-green" to={ROUTES.configureVendors.path}>
              Vendor Spend Page
            </Link>
            .
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) =>
          numberFormat(getValue<number>(), { isCurrency: true, precision: 0 }),
      },
      {
        header: t`$/Visit`,
        id: 'spendPerTargetVisit',
        accessorFn: (row) => row.spendPerTargetVisit,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('spendPerTargetVisit'),
        size: 95,
        infoPopover: (
          <Trans>
            $/Visit shows the cost per audience visit (Spend / Audience Visits). Spend is reported
            either through your vendor's API connection or from manually calculated spend entered by
            your team on the{' '}
            <Link variant="body2" color="static-green" to={ROUTES.configureVendors.path}>
              Vendor Spend Page
            </Link>
            .
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) =>
          numberFormat(getValue<number>(), { isCurrency: true, precision: 2 }),
      },
      {
        header: t`Impressions`,
        id: 'adImpressions',
        accessorFn: (row) => row.adImpressions,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('adImpressions'),
        size: 120,
        infoPopover: (
          <Trans>
            Volume of impressions shared with Channel99 through the ads integration. This is total
            impressions for the time period and should match your vendors reporting.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row, getValue }) =>
          +getValue<number>() > 0 ? (
            <Link
              variant="caption1"
              to={ROUTES.webTrafficActivityWithParams({
                'w.keywordIds': [row.original.ad.id],
                'w.metrics.impressions': '[1,]',
              })}
            >
              {numberFormat(getValue<number>())}
            </Link>
          ) : (
            0
          ),
      },
      {
        header: t`Clicks`,
        id: 'adClicks',
        accessorFn: (row) => row.adClicks,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('adClicks'),
        size: 90,
        infoPopover: (
          <Trans>
            Clicks reported directly from your connected vendor. This should match what your vendor
            is reporting.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) => numberFormat(getValue<number>()),
      },
      {
        header: includeViewThroughVisits ? t`Visits + View Through` : t`Visits`,
        id: 'visits',
        enableSorting: sortFields.includes('visits'),
        size: 300,
        actionContent: (
          <div className="flex items-center gap-1 py-0.5">
            <Toggle
              variant="ghost"
              size="small"
              shape="square"
              pressed={includeViewThroughVisits}
              aria-label={t`Include view-through visits in chart`}
              onPressedChange={setIncludeViewThroughVisits}
            >
              <Trans>VT</Trans>
            </Toggle>
            <StackModeToggleButton mode={visitsMode} size="small" onChange={setVisitsMode} />
          </div>
        ),
        infoPopover: (
          <Trans>
            Volume of impressions shared with Channel99 through the ads integration. This is total
            impressions for the time period and should match your vendors reporting.{' '}
            <SupportLink variant="body2" color="static-green" path="VISITS">
              More details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => (
          <VisitsTableBarCell
            data={[
              {
                type: audience?.isTAM ? TRAFFIC_TYPES.TAM : TRAFFIC_TYPES.AUDIENCE,
                label: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
                value: includeViewThroughVisits
                  ? row.original.targetVisits + (row.original.targetViewThroughVisits ?? 0)
                  : row.original.targetVisits,
                color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
                disabled: audience?.isTAM
                  ? disabledSeries?.includes(TRAFFIC_TYPES.TAM) === true
                  : disabledSeries?.includes(TRAFFIC_TYPES.AUDIENCE) === true,
              },
              {
                type: TRAFFIC_TYPES.OTHER,
                label: t`Other Companies`,
                value: includeViewThroughVisits
                  ? row.original.otherVisits + (row.original.otherViewThroughVisits ?? 0)
                  : row.original.otherVisits,
                color: VISITS_PRIMARY,
                disabled: disabledSeries?.includes(TRAFFIC_TYPES.OTHER) === true,
              },
              {
                type: TRAFFIC_TYPES.BOT,
                label: t`Bot`,
                value: includeViewThroughVisits
                  ? row.original.botVisits + (row.original.botViewThroughVisits ?? 0)
                  : row.original.botVisits,
                color: BOT_PRIMARY,
                disabled: disabledSeries?.includes(TRAFFIC_TYPES.BOT) === true,
              },
              {
                type: TRAFFIC_TYPES.UNRESOLVED,
                label: t`Unknown`,
                value: includeViewThroughVisits
                  ? row.original.unresolvedVisits + (row.original.unresolvedViewThroughVisits ?? 0)
                  : row.original.unresolvedVisits,
                color: UNKNOWN_PRIMARY,
                disabled: disabledSeries?.includes(TRAFFIC_TYPES.UNRESOLVED) === true,
              },
            ]}
            maxVisits={visitsBarMax}
            displayMode={visitsMode}
            isOpaque={
              (stickyRow == null && hoveredRow == null) ||
              stickyRow?.record === row.original ||
              hoveredRow === row.original
            }
            isTooltipOpen={hoveredRow === row.original || stickyRow?.record === row.original}
            isTooltipSticky={stickyRow?.record === row.original}
            tooltipTitle={row.original.ad.name}
            onStickyTooltipClose={() => setStickyRow(null)}
          />
        ),
        cellProps: ({ row }) => {
          const record = row.original;
          return {
            style: { cursor: 'pointer' },
            onMouseEnter: () => setHoveredRow(record),
            onMouseLeave: () => setHoveredRow(null),
            onClick: (e) => {
              if (didClickOnStickyPopover(e.target)) {
                return;
              }

              // clicking on the same row again will close the tooltip
              if (stickyRow?.record === record) {
                setStickyRow(null);
              } else {
                setStickyRow({
                  element: e.currentTarget as HTMLElement,
                  record,
                });
              }
            },
          };
        },
      },
      {
        header: t`VT %`,
        id: 'viewThroughPercent',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('viewThroughPercent'),
        size: 80,
        infoPopover: (
          <Trans>
            VT % shows the share of visits that were view-through only. A view-through visit happens
            when someone views tracked content and later visits through a different direct source
            without clicking that tracked content. This metric is calculated as Total VT Visits /
            (Total VT Visits + Total Visits).
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter type="percent" {...props} />,
        cell: ({ row }) => (
          <ViewThroughPercentCell
            tooltipTitle={row.original.ad.name}
            viewThroughPercent={row.original.viewThroughPercent}
            rows={[
              {
                label: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
                color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
                viewThroughVisits: row.original.targetViewThroughVisits ?? 0,
              },
              {
                label: t`Other Companies`,
                color: VISITS_PRIMARY,
                viewThroughVisits: row.original.otherViewThroughVisits ?? 0,
              },
              {
                label: t`Bot`,
                color: BOT_PRIMARY,
                viewThroughVisits: row.original.botViewThroughVisits ?? 0,
              },
              {
                label: t`Unknown`,
                color: UNKNOWN_PRIMARY,
                viewThroughVisits: row.original.unresolvedViewThroughVisits ?? 0,
              },
            ]}
          />
        ),
      },
      {
        header: t`Visit Efficiency`,
        id: 'visitEfficiency',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('visitEfficiency'),
        size: 115,
        infoPopover: (
          <Trans>
            Visit efficiency is the ratio of target audience visits to total visits (unknown
            removed) to give a view of how well a keyword attracts the right audience.{' '}
            <SupportLink variant="body2" color="static-green" path="VISIT_EFFICIENCY">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter type="percent" {...props} />,
        cell: ({ row }) =>
          numberFormat(row.original.visitEfficiency, {
            isPercent: true,
            precision: 1,
          }),
      },
      {
        header: t`Engaged Companies`,
        id: 'engagedCompanies',
        accessorFn: (row) => row.engagedCompanies,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('engagedCompanies'),
        size: 115,
        infoPopover: (
          <Trans>Engaged companies are a count of companies which had at least one visit.</Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row, getValue }) =>
          +getValue<number>() > 0 ? (
            <Link
              variant="caption1"
              to={ROUTES.webTrafficActivityWithParams({
                'w.keywordIds': row.original.ad.id,
                'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
                'w.inTam': audience?.isTAM ? 'true' : undefined,
                'w.metrics.visits': '[1,]',
              })}
            >
              {numberFormat(getValue<number>())}
            </Link>
          ) : (
            0
          ),
      },
      {
        header: t`Leads Influence`,
        id: 'leadsInfluenced',
        accessorFn: (row) => row.leadsInfluenced,
        size: 115,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('leadsInfluenced'),
        infoPopover: (
          <Trans>
            Lead influence is given to each keyword with at least one visit during the opportunity
            influence window chosen before the opportunity create date.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) => numberFormat(getValue<number>()),
      },
      {
        header: t`Pipeline Influence`,
        id: 'pipelineInfluenced',
        accessorFn: (row) => row.pipelineInfluenced,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('pipelineInfluenced'),
        size: 120,
        infoPopover: (
          <Trans>
            Pipeline influence is given to each keyword with at least one visit during the
            opportunity influence window chosen before the opportunity create date.{' '}
            <SupportLink variant="body2" color="static-green" path="PIPELINE_INFLUENCE">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) =>
          numberFormat(getValue<number>(), { isCurrency: true, precision: 0 }),
      },
      {
        header: t`# Pipeline Influenced`,
        id: 'countPipelineInfluenced',
        accessorFn: (row) => row.countPipelineInfluenced,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('countPipelineInfluenced'),
        size: 145,
        infoPopover: (
          <Trans>Number of opportunities with pipeline influence attributed to this keyword.</Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) => numberFormat(getValue<number>()),
      },
      {
        header: t`Closed Won Influence`,
        id: 'closedWonInfluenced',
        accessorFn: (row) => row.closedWonInfluenced,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('closedWonInfluenced'),
        size: 120,
        infoPopover: (
          <Trans>
            Closed won influence is given to each keyword with at least one visit during the
            opportunity influence window chosen before the opportunity create date.{' '}
            <SupportLink variant="body2" color="static-green" path="CLOSED_WON_INFLUENCE">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) =>
          numberFormat(getValue<number>(), { isCurrency: true, precision: 0 }),
      },
      {
        header: t`# Closed Won Influenced`,
        id: 'countClosedwonInfluenced',
        accessorFn: (row) => row.countClosedwonInfluenced,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('countClosedwonInfluenced'),
        size: 160,
        infoPopover: (
          <Trans>
            Number of closed-won opportunities with influence attributed to this keyword.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) => numberFormat(getValue<number>()),
      },
      {
        header: t`Linear Pipeline`,
        id: 'pipelineLinearAttribution',
        accessorFn: (row) => row.pipelineLinearAttribution,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('pipelineLinearAttribution'),
        size: 120,
        infoPopover: (
          <Trans>
            Linear pipeline is given to each keyword with at least one visit during the opportunity
            influence window chosen before the opportunity create date.{' '}
            <SupportLink variant="body2" color="static-green" path="PIPELINE_LINEAR_ATTRIBUTION">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) =>
          numberFormat(getValue<number>(), { isCurrency: true, precision: 0 }),
      },
      {
        header: t`Linear Closed Won`,
        id: 'closedWonLinearAttribution',
        accessorFn: (row) => row.closedWonLinearAttribution,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('closedWonLinearAttribution'),
        size: 120,
        infoPopover: (
          <Trans>
            Linear closed won is given to each keyword with at least one visit during the
            opportunity influence window chosen before the opportunity create date.{' '}
            <SupportLink variant="body2" color="static-green" path="CLOSED_WON_LINEAR_ATTRIBUTION">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) =>
          numberFormat(getValue<number>(), { isCurrency: true, precision: 0 }),
      },
      {
        header: t`First-Touch Pipeline`,
        id: 'pipelineFirstTouchAttribution',
        accessorFn: (row) => row.pipelineFirstTouchAttribution,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('pipelineFirstTouchAttribution'),
        size: 120,
        infoPopover: (
          <Trans>
            The first keyword which was interacted with from the beginning of the influence window
            to the opportunity create date.{' '}
            <SupportLink
              variant="body2"
              color="static-green"
              path="PIPELINE_FIRST_TOUCH_ATTRIBUTION"
            >
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) =>
          numberFormat(getValue<number>(), { isCurrency: true, precision: 0 }),
      },
      {
        header: t`First-Touch Closed Won`,
        id: 'closedWonFirstTouchAttribution',
        accessorFn: (row) => row.closedWonFirstTouchAttribution,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('closedWonFirstTouchAttribution'),
        size: 120,
        infoPopover: (
          <Trans>
            The first keyword which was interacted with from the beginning of the influence window
            to the opportunity close date.{' '}
            <SupportLink
              variant="body2"
              color="static-green"
              path="CLOSED_WON_FIRST_TOUCH_ATTRIBUTION"
            >
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) =>
          numberFormat(getValue<number>(), { isCurrency: true, precision: 0 }),
      },
      {
        header: t`Last-Touch Pipeline`,
        id: 'pipelineLastTouchAttribution',
        accessorFn: (row) => row.pipelineLastTouchAttribution,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('pipelineLastTouchAttribution'),
        size: 120,
        infoPopover: (
          <Trans>
            The keyword with the previous interaction to the create date of the opportunity.{' '}
            <SupportLink
              variant="body2"
              color="static-green"
              path="PIPELINE_LAST_TOUCH_ATTRIBUTION"
            >
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ getValue }) =>
          numberFormat(getValue<number>(), { isCurrency: true, precision: 0 }),
      },
    ],
    [
      sortFields,
      audience,
      disabledSeries,
      visitsBarMax,
      hoveredRow,
      stickyRow,
      visitsMode,
      includeViewThroughVisits,
      setVisitsMode,
      setIncludeViewThroughVisits,
    ],
  );
  return (
    <TableState
      tableKey="paidSearchKeywordActivity"
      columns={columns}
      defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
    >
      <PaidSearchKeywordActivityTable
        columns={columns}
        selectedAds={selectedAds}
        isMaxCompareLimit={isMaxCompareLimit}
        data={data}
        totalResults={totalResults}
        isLoading={isLoading}
        error={error}
        menuSelectorOpen={menuSelectorOpen}
        advancedFiltersOpen={advancedFiltersOpen}
        onMetricSelectorOpen={onMetricSelectorOpen}
        onAdvancedFiltersOpen={onAdvancedFiltersOpen}
        onSelectedAdsChange={setSelectedAds}
        onScoreAds={onScoreAds}
      />
    </TableState>
  );
};

export default PaidSearchKeywordActivityTableContainer;
